import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, X, Upload, Sparkles, Loader2 } from "lucide-react";

const EMPTY = {
  name: "", player: "", team: "", season: "", description: "",
  image_url: "", additional_images: [],
  price: 30, stock: 10, sizes: "S,M,L,XL", has_long_sleeve: false, featured: false,
};

export default function AdminJerseys() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [generatingId, setGeneratingId] = useState(null);
  const [priceDraft, setPriceDraft] = useState({}); // { [id]: "42.50" }
  const [savingPrice, setSavingPrice] = useState(null);
  const [togglingSleeve, setTogglingSleeve] = useState(null);
  const fileInputRef = useRef(null);
  const addImageRef = useRef(null);

  const load = async () => {
    const r = await api.get("/jerseys");
    setItems(r.data);
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (j) => {
    setEditing(j.id);
    setForm({
      name: j.name, player: j.player, team: j.team, season: j.season, description: j.description,
      image_url: j.image_url, additional_images: j.additional_images || [],
      price: j.price, stock: j.stock,
      sizes: (j.sizes || []).join(","), has_long_sleeve: !!j.has_long_sleeve, featured: !!j.featured,
    });
    setOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock),
        sizes: form.sizes.split(",").map(s => s.trim()).filter(Boolean),
      };
      if (editing) await api.put(`/jerseys/${editing}`, payload);
      else await api.post("/jerseys", payload);
      toast.success(editing ? "Jersey updated" : "Jersey added");
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm("Delete this jersey?")) return;
    await api.delete(`/jerseys/${id}`);
    toast.success("Jersey removed");
    load();
  };

  const handleFile = async (e, target) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (target === "main") {
        setForm((f) => ({ ...f, image_url: r.data.url }));
        toast.success("Main image uploaded");
      } else {
        setForm((f) => ({ ...f, additional_images: [...(f.additional_images || []), r.data.url] }));
        toast.success("Image added");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeAdditional = (idx) => {
    setForm((f) => ({
      ...f,
      additional_images: f.additional_images.filter((_, i) => i !== idx),
    }));
  };

  const generateMockup = async (id) => {
    setGeneratingId(id);
    try {
      const r = await api.post(`/jerseys/${id}/generate-back-mockup`);
      toast.success("Back mockup generated");
      // Update local row & if editing, sync form
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, additional_images: r.data.additional_images } : it));
      if (editing === id) {
        setForm((f) => ({ ...f, additional_images: r.data.additional_images }));
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Mockup failed");
    } finally {
      setGeneratingId(null);
    }
  };

  const savePrice = async (id) => {
    const raw = priceDraft[id];
    const n = parseFloat(raw);
    if (Number.isNaN(n) || n < 0) { toast.error("Invalid price"); return; }
    setSavingPrice(id);
    try {
      const r = await api.put(`/jerseys/${id}`, { price: n });
      setItems((prev) => prev.map((it) => it.id === id ? { ...it, price: r.data.price } : it));
      setPriceDraft((d) => { const c = { ...d }; delete c[id]; return c; });
      toast.success(`Price → £${n.toFixed(2)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Price update failed");
    } finally {
      setSavingPrice(null);
    }
  };

  const toggleLongSleeve = async (j) => {
    setTogglingSleeve(j.id);
    try {
      const r = await api.put(`/jerseys/${j.id}`, { has_long_sleeve: !j.has_long_sleeve });
      setItems((prev) => prev.map((it) => it.id === j.id ? { ...it, has_long_sleeve: r.data.has_long_sleeve } : it));
      toast.success(r.data.has_long_sleeve ? "Long sleeve enabled" : "Long sleeve disabled");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setTogglingSleeve(null);
    }
  };

  return (
    <div data-testid="admin-jerseys">
      <div className="flex items-end justify-between">
        <div>
          <div className="font-mono-retro text-xs tracking-[0.4em] text-[#C8A96A] uppercase">The Rail</div>
          <h1 className="font-display text-4xl text-[#F2EBDD] mt-1">Manage Jerseys</h1>
        </div>
        <button data-testid="new-jersey-btn" onClick={openNew} className="btn-primary">
          <Plus size={16} /> Add Jersey
        </button>
      </div>

      <div className="mt-8 border border-[#332D2B] bg-[#1C1816] overflow-x-auto">
        <table className="w-full">
          <thead className="border-b border-[#332D2B]">
            <tr className="text-left font-mono-retro text-[10px] tracking-[0.3em] text-[#756F6B] uppercase">
              <th className="p-4">Image</th>
              <th className="p-4">Name</th>
              <th className="p-4">Team · Season</th>
              <th className="p-4">Price</th>
              <th className="p-4">Long Slv</th>
              <th className="p-4">Stock</th>
              <th className="p-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map((j) => {
              const draft = priceDraft[j.id];
              const dirty = draft !== undefined && parseFloat(draft) !== Number(j.price);
              return (
              <tr key={j.id} className="border-b border-[#332D2B] hover:bg-[#2A2320]">
                <td className="p-4"><img src={j.image_url} alt="" className="w-12 h-12 object-cover border border-[#332D2B]" /></td>
                <td className="p-4 text-[#F2EBDD]">
                  <div>{j.name}</div>
                  <div className="text-[11px] text-[#756F6B]">{j.player}</div>
                </td>
                <td className="p-4 text-[#A8A39D] text-sm">{j.team} · {j.season}</td>
                <td className="p-4">
                  <div className="flex items-center gap-2">
                    <span className="text-[#C8A96A] font-mono-retro text-xs">£</span>
                    <input
                      data-testid={`price-input-${j.id}`}
                      type="number"
                      step="0.01"
                      min="0"
                      className="w-20 bg-[#0F0C0B] border border-[#332D2B] focus:border-[#C8A96A] outline-none text-[#F2EBDD] text-sm px-2 py-1"
                      value={draft !== undefined ? draft : j.price}
                      onChange={(e) => setPriceDraft((d) => ({ ...d, [j.id]: e.target.value }))}
                    />
                    {dirty && (
                      <button
                        data-testid={`save-price-${j.id}`}
                        onClick={() => savePrice(j.id)}
                        disabled={savingPrice === j.id}
                        className="text-[#C8A96A] hover:text-[#F2EBDD] font-mono-retro text-[10px] tracking-[0.2em] uppercase disabled:opacity-40"
                      >
                        {savingPrice === j.id ? "…" : "Save"}
                      </button>
                    )}
                  </div>
                </td>
                <td className="p-4">
                  <button
                    data-testid={`toggle-long-sleeve-${j.id}`}
                    onClick={() => toggleLongSleeve(j)}
                    disabled={togglingSleeve === j.id}
                    className={`font-mono-retro text-[10px] tracking-[0.2em] uppercase px-2 py-1 border transition-colors ${
                      j.has_long_sleeve
                        ? "border-[#7FB77E] text-[#7FB77E]"
                        : "border-[#332D2B] text-[#756F6B] hover:border-[#A8A39D]"
                    } disabled:opacity-40`}
                    title="Toggle long sleeve availability"
                  >
                    {j.has_long_sleeve ? "✓ Yes" : "No"}
                  </button>
                </td>
                <td className="p-4 text-[#A8A39D]">{j.stock}</td>
                <td className="p-4 text-right space-x-2 whitespace-nowrap">
                  <button
                    data-testid={`mockup-jersey-${j.id}`}
                    onClick={() => generateMockup(j.id)}
                    disabled={generatingId === j.id}
                    className="text-[#A8A39D] hover:text-[#C8A96A] inline-flex items-center gap-1 text-sm disabled:opacity-50"
                    title="Generate AI back mockup"
                  >
                    {generatingId === j.id ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />} Mockup
                  </button>
                  <button data-testid={`edit-jersey-${j.id}`} onClick={() => openEdit(j)} className="text-[#A8A39D] hover:text-[#C8A96A] inline-flex items-center gap-1 text-sm"><Edit3 size={14} /> Edit</button>
                  <button data-testid={`delete-jersey-${j.id}`} onClick={() => remove(j.id)} className="text-[#A8A39D] hover:text-[#8B4530] inline-flex items-center gap-1 text-sm"><Trash2 size={14} /> Delete</button>
                </td>
              </tr>
              );
            })}
            {items.length === 0 && (
              <tr><td colSpan={7} className="p-10 text-center text-[#A8A39D]">No jerseys yet. Add your first shirt.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#0F0C0B]/80 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="border-2 border-[#332D2B] bg-[#1C1816] max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-5 border-b border-[#332D2B]">
              <div className="font-display text-2xl text-[#F2EBDD]">{editing ? "Edit Jersey" : "New Jersey"}</div>
              <button onClick={() => setOpen(false)} className="text-[#A8A39D] hover:text-[#F2EBDD]"><X size={20} /></button>
            </div>
            <form onSubmit={save} className="p-6 space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="fz-label">Name</label>
                  <input data-testid="jersey-form-name" required className="fz-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </div>
                <div>
                  <label className="fz-label">Player</label>
                  <input data-testid="jersey-form-player" required className="fz-input" value={form.player} onChange={(e) => setForm({ ...form, player: e.target.value })} />
                </div>
                <div>
                  <label className="fz-label">Team</label>
                  <input data-testid="jersey-form-team" required className="fz-input" value={form.team} onChange={(e) => setForm({ ...form, team: e.target.value })} />
                </div>
                <div>
                  <label className="fz-label">Season</label>
                  <input data-testid="jersey-form-season" required className="fz-input" value={form.season} onChange={(e) => setForm({ ...form, season: e.target.value })} placeholder="26/27" />
                </div>
              </div>
              <div>
                <label className="fz-label">Description</label>
                <textarea data-testid="jersey-form-description" rows={3} className="fz-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>

              {/* Main image */}
              <div>
                <label className="fz-label">Main Image</label>
                <div className="flex gap-2">
                  <input
                    data-testid="jersey-form-image"
                    required
                    className="fz-input flex-1"
                    value={form.image_url}
                    onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    placeholder="Paste URL or upload →"
                  />
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => handleFile(e, "main")}
                  />
                  <button
                    type="button"
                    data-testid="jersey-form-upload"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={uploading}
                    className="btn-secondary text-xs"
                  >
                    {uploading ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
                  </button>
                </div>
                {form.image_url && (
                  <div className="mt-3 w-32 h-32 border border-[#332D2B] bg-[#2A2320] overflow-hidden">
                    <img src={form.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
              </div>

              {/* Additional images */}
              <div>
                <label className="fz-label">Additional Images (back view, detail shots…)</label>
                <input
                  ref={addImageRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFile(e, "additional")}
                />
                <div className="flex flex-wrap gap-2">
                  {(form.additional_images || []).map((src, i) => (
                    <div key={i} className="relative w-20 h-20 border border-[#332D2B] bg-[#2A2320] overflow-hidden group">
                      <img src={src} alt={`extra ${i + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        onClick={() => removeAdditional(i)}
                        className="absolute top-1 right-1 bg-[#0F0C0B]/80 border border-[#8B4530] text-[#F2EBDD] hover:bg-[#8B4530] w-5 h-5 flex items-center justify-center"
                        title="Remove"
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                  <button
                    type="button"
                    data-testid="jersey-form-add-image"
                    onClick={() => addImageRef.current?.click()}
                    disabled={uploading}
                    className="w-20 h-20 border border-dashed border-[#332D2B] hover:border-[#C8A96A] text-[#A8A39D] hover:text-[#C8A96A] flex flex-col items-center justify-center gap-1 transition-colors"
                  >
                    {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />}
                    <span className="font-mono-retro text-[9px] tracking-[0.2em] uppercase">Add</span>
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="fz-label">Price (£)</label>
                  <input data-testid="jersey-form-price" type="number" step="0.01" required className="fz-input" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} />
                </div>
                <div>
                  <label className="fz-label">Stock</label>
                  <input data-testid="jersey-form-stock" type="number" required className="fz-input" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} />
                </div>
                <div>
                  <label className="fz-label">Sizes (CSV)</label>
                  <input data-testid="jersey-form-sizes" className="fz-input" value={form.sizes} onChange={(e) => setForm({ ...form, sizes: e.target.value })} placeholder="S,M,L,XL" />
                </div>
              </div>
              <div className="flex flex-wrap gap-5">
                <label className="inline-flex items-center gap-2 text-[#F2EBDD]">
                  <input data-testid="jersey-form-long-sleeve" type="checkbox" checked={form.has_long_sleeve} onChange={(e) => setForm({ ...form, has_long_sleeve: e.target.checked })} className="accent-[#C8A96A]" />
                  <span className="font-mono-retro text-xs tracking-[0.2em] uppercase">Long Sleeve Available</span>
                </label>
                <label className="inline-flex items-center gap-2 text-[#F2EBDD]">
                  <input data-testid="jersey-form-featured" type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} className="accent-[#C8A96A]" />
                  <span className="font-mono-retro text-xs tracking-[0.2em] uppercase">Featured</span>
                </label>
              </div>
              <div className="flex gap-3 pt-3">
                <button data-testid="jersey-form-save" disabled={saving} className="btn-primary">{saving ? "Saving…" : "Save Jersey"}</button>
                <button type="button" onClick={() => setOpen(false)} className="btn-ghost">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
